import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from '@chicare/shared';

import './styles/tokens.css';
import { App } from './app/App';

/**
 * Query defaults tuned for Nigerian mobile networks.
 *
 * Retrying a 422 is pointless and retrying a 401 is worse than pointless, so
 * only genuinely transient failures are retried. Two attempts, not five: a
 * buyer on a stalled connection would rather see the offline state and decide
 * for themselves than watch a spinner for thirty seconds.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && !error.isRetryable) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // Money-mutating calls carry an Idempotency-Key, so a retry is safe —
      // but the decision to retry belongs to the screen, not to a global.
      retry: false,
    },
  },
});

// Dev-only: makes the query cache inspectable from the console, which is the
// difference between "the screen is stuck" and "the query is disabled because
// X". Stripped from production by the DEV guard.
if (import.meta.env.DEV) {
  (window as unknown as { __queryClient: QueryClient }).__queryClient = queryClient;
}

/**
 * The service worker, and the ones that are not ours.
 *
 * ── WHY THIS ALSO EVICTS STRANGERS ──────────────────────────────────────────
 *
 * A service worker belongs to an ORIGIN, not to a project, and it outlives the
 * code that registered it. Two apps developed on the same machine share
 * `localhost:5173`, so one project's worker goes on intercepting whatever is
 * served there next — including this app, including its offline page, under
 * somebody else's brand.
 *
 * That is not hypothetical: it is how a different company's offline screen came
 * to be shown to somebody opening ChiCare. The loop below unregisters any
 * worker on this origin whose script is not ours, in dev and in production
 * both, because the production case (a worker left behind by whatever used to
 * be on this domain) is the one nobody can debug from the outside.
 *
 * ── AND WHY IT IS REGISTERED AT ALL ─────────────────────────────────────────
 *
 * For one page: a ChiCare-branded screen when the network is gone, instead of
 * the browser's own error. See public/sw.js for what it will and will not
 * cache, and why the answer is "almost nothing" on a shop that sells medicine.
 */
function manageServiceWorkers(): void {
  if (!('serviceWorker' in navigator)) return;

  const ours = new URL('/sw.js', window.location.origin).href;

  void navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => {
      for (const registration of registrations) {
        const script =
          registration.active?.scriptURL ??
          registration.waiting?.scriptURL ??
          registration.installing?.scriptURL;

        // Anything on this origin that is not /sw.js is a leftover. Evicting it
        // is safe: a worker that belongs to another app has nothing of ours to
        // lose, and a stale one of ours is replaced a line later.
        if (script && script !== ours) {
          void registration.unregister();
        }
      }
    })
    .catch(() => undefined);

  // Registered after load so it never competes with the first paint for
  // bandwidth on a 3G connection.
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register(ours).catch(() => {
      // A refused registration is not worth a broken app. Private mode and
      // some embedded webviews simply do not allow one, and everything except
      // the offline page works identically without it.
    });
  });
}

manageServiceWorkers();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
